/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

'use client';

import { useEffect, useRef, useState } from 'react';
import { useLanguage } from '@/providers/LanguageProvider';
import { measuresManager } from '@/services/measuresManager';
import { getTenantTimeRange } from '@/utils/getTenantTimeRange';
import { AppListItem } from 'types/app-list';
import { FilterType } from 'types/measures-api-data';

import { useApplications } from '@/hooks/useApplications';
import { useAuth } from '@/hooks/useAuth';
import { useMeasuresFromLocalStorage } from '@/hooks/useMeasuresFromLocalStorage';
import { useStartTour } from '@/hooks/useStartTour';
import { usePresets } from '@/components/display-page/prompts/date-range-picker/calendar-helper/presets';

import { useTenantId } from '../settings-page/hooks/useTenantId';
import { Guide } from '../tour-guide';
import { ApplicationsSkeleton } from './app-cards-skeleton';
import { AppFilter } from './app-filter';
import CustomApplications from './custom-applications';
import DemoApplications from './demo-applicatons';

export const DisplayAppCards = () => {
  const [showExamples, setShowExamples] = useState(() => {
    if (typeof window !== 'undefined') {
      const storedValue = localStorage.getItem('showExamples');
      return storedValue !== null ? JSON.parse(storedValue) : true;
    }
    return true;
  });
  const { tenantId } = useTenantId();

  // Add filter state for error/performance filtering
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');

  const {
    cardMeasures,
    loading: measuresLoading,
    refreshMeasures,
  } = useMeasuresFromLocalStorage();

  // This will hold apps that need measures API call (new apps or all apps for first time)
  const [appsForMeasures, setAppsForMeasures] = useState<string[]>([]);
  const [isFirstLoad, setIsFirstLoad] = useState(false);

  const [allAppNames, setAllAppNames] = useState<string[]>([]);
  const isFirstMount = useRef(true);

  const {
    apps,
    filteredCustomApplications,
    selectedApps,
    setSelectedApps,
    loading,
  } = useApplications();

  const { auth } = useAuth();
  const { startTour, setStartTour } = useStartTour();
  const { locale } = useLanguage();
  const presets = usePresets();

  const handleEndTour = (finished: boolean) => {
    setStartTour(false);
    if (finished) {
      window.dispatchEvent(new Event('open-kahu-chat'));
    }
  };

  // Removed all the measures and apps data from localStorage if user doesn't have accessToken.
  useEffect(() => {
    if (!auth?.accessToken) {
      localStorage.removeItem('measuresList');
      localStorage.removeItem('measuresLastFetch');
      localStorage.removeItem('appsList');
      measuresManager.destroy(); // Stop the interval
    }
  }, [auth?.accessToken]);

  // Initialize measures manager on mount
  useEffect(() => {
    if (allAppNames.length > 0 && auth?.accessToken) {
      const tenantTimeRange = getTenantTimeRange();
      const lastTimeRange = measuresManager.getLastTimeRange();

      // If tenant default time range changed, force refresh
      if (tenantTimeRange !== lastTimeRange) {
        measuresManager.reset();
      }

      measuresManager.initialize(allAppNames, presets);
      isFirstMount.current = false; // Mark as initialized
    }
  }, [allAppNames, auth?.accessToken, presets]);

  // Check if refresh needed when user navigates back to home page
  // This will only run on subsequent mounts (not the first one)
  useEffect(() => {
    if (
      !isFirstMount.current &&
      allAppNames.length > 0 &&
      tenantId &&
      measuresManager.needsRefresh()
    ) {
      refreshMeasures(allAppNames);
    }
  }, []);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('showExamples', JSON.stringify(showExamples));
    }
  }, [showExamples]);

  useEffect(() => {
    if (typeof window !== 'undefined' && auth?.accessToken) {
      const storedAccessToken = localStorage.getItem('accessToken');
      if (storedAccessToken !== auth.accessToken) {
        localStorage.setItem('accessToken', auth.accessToken);
      }
    }
  }, [auth?.accessToken]);

  useEffect(() => {
    const value = localStorage.getItem('NEW_OKAHU_USER');
    if (value == 'true') {
      setStartTour(true);
      localStorage.removeItem('NEW_OKAHU_USER');
    }
  }, []);

  useEffect(() => {
    if (loading) return;

    // Get current apps from provider data
    const currentAppsNames: string[] =
      apps
        ?.map((app: AppListItem) => app.name)
        .filter((name: string) => Boolean(name)) || [];

    // Store for manager
    setAllAppNames(currentAppsNames);

    // Get stored apps from localStorage
    let storedAppsNames: string[] = [];
    const storedAppsList = localStorage.getItem('appsList');

    if (storedAppsList) {
      const parsedList = JSON.parse(storedAppsList);
      // Ensure it's an array, converting from an array-like object if necessary
      storedAppsNames = Array.isArray(parsedList)
        ? parsedList
        : Object.values(parsedList);
    }

    // Check if this is the first load (no apps in localStorage)
    const isFirstTime = storedAppsNames.length === 0;
    setIsFirstLoad(isFirstTime);

    if (isFirstTime) {
      // First time -- store all apps and set them for measures API call
      localStorage.setItem('appsList', JSON.stringify(currentAppsNames));
      setAppsForMeasures(currentAppsNames);
    } else {
      // Subsequent loads -- find new apps that aren't in localStorage
      const newApps: string[] = currentAppsNames.filter(
        (appName: string) => !storedAppsNames.includes(appName)
      );
      if (newApps.length > 0) {
        // Add new apps to localStorage
        localStorage.setItem('appsList', JSON.stringify(currentAppsNames));

        // Set only new apps for measures API call
        setAppsForMeasures(newApps);
      } else {
        // No new apps, no measures API call needed
        setAppsForMeasures([]);
      }
    }
  }, [apps, loading]);

  const demoApplications = apps.filter((app: AppListItem) => app.is_playground);
  const hasExampleStep = showExamples && demoApplications.length > 0;

  if (loading) {
    return <ApplicationsSkeleton />;
  }

  return (
    <div>
      {startTour && (
        <Guide
          start={startTour}
          setStartTour={setStartTour}
          onTourEnd={handleEndTour}
          hasExampleStep={hasExampleStep}
        />
      )}

      <AppFilter
        data={apps}
        selectedApps={selectedApps}
        setSelectedApps={setSelectedApps}
        showExamples={showExamples}
        setShowExamples={setShowExamples}
        appsForMeasures={appsForMeasures}
        isFirstLoad={isFirstLoad}
        activeFilter={activeFilter}
        setActiveFilter={setActiveFilter}
        measuresData={cardMeasures} // Pass the measures data from hook
        loading={measuresLoading} // Pass loading state
      />

      <div className="mt-6 flex w-full gap-5 pb-10">
        {/* Your Applications Section */}
        <CustomApplications
          apps={filteredCustomApplications}
          locale={locale}
          className={showExamples ? 'w-2/3' : 'w-full'}
          showExamples={showExamples}
          measuresData={cardMeasures} // Use from measures hook
          activeFilter={activeFilter}
          loading={measuresLoading} // Pass loading state
        />

        {/* Examples Section */}
        {showExamples && (
          <>
            <div className="-mb-4 border-l border-line-tertiary"></div>
            <DemoApplications
              apps={demoApplications}
              locale={locale}
              measuresData={cardMeasures} // Use from measures hook
              activeFilter={activeFilter}
              loading={measuresLoading} // Pass loading state
            />
          </>
        )}
      </div>
    </div>
  );
};
